import { hydrateRoot, createRoot } from 'react-dom/client';

/** Track roots per container */
const appRoots = new WeakMap();
const clientRoots = new WeakMap();

/** Track containers that have been hydrated once */
const hydratedContainers = new WeakSet();

/**
 * First call on a container with server markup hydrates; later calls (after unmount) client-render.
 */
export function renderAppRoot(container, element) {
  const existingRoot = appRoots.get(container);

  if (existingRoot) {
    // Re-render the same root
    existingRoot.render(element);
    return;
  }

  // First render on this container
  if (hydratedContainers.has(container)) {
    // Container was previously hydrated and unmounted; use createRoot
    const root = createRoot(container);
    root.render(element);
    appRoots.set(container, root);
  } else {
    // First time; hydrate
    const root = hydrateRoot(container, element, {
      onRecoverableError: (error, errorInfo) => {
        // the component stack shows where the server and client trees diverged
        // eslint-disable-next-line no-console
        console.error('[hydration]', error, errorInfo?.componentStack ?? '');
      }
    });
    appRoots.set(container, root);
    hydratedContainers.add(container);
  }
}

/**
 * Unmount the app root.
 */
export function unmountAppRoot(container) {
  const root = appRoots.get(container);
  if (root) {
    root.unmount();
    appRoots.delete(container);
  }
}

/**
 * Always createRoot (editor, MFE manager). Re-calling on the same container re-renders the existing root.
 */
export function renderClientRoot(container, element) {
  const existingRoot = clientRoots.get(container);

  if (existingRoot) {
    // Re-render the same root
    existingRoot.render(element);
    return;
  }

  // Create new root
  const root = createRoot(container);
  root.render(element);
  clientRoots.set(container, root);
}

/**
 * Unmount the client root.
 */
export function unmountClientRoot(container) {
  const root = clientRoots.get(container);
  if (root) {
    root.unmount();
    clientRoots.delete(container);
  }
}
